import type { AppEdge } from '@/edges';
import type { AppNode } from '@/nodes';
import type { AppFlowObjectKnown } from '@/util/AppFlowObjectKnown';
import { isEqual } from 'lodash';

/**
 * Where one draft guide stands relative to what the app is actually serving.
 *
 * Every field is a comparison against context-level facts — the one live guide and the one staged
 * preview — so a whole list of guides can be judged from a single pair of reads. That's what lets
 * the Guides table show the same badges the editor does without a query per row.
 */
export type FlowStatus = {
    /** This draft IS the guide the app serves. */
    published: boolean;
    /** This draft is staged in preview and not yet live. */
    preview: boolean;
    /** This draft's content matches the live guide's — nothing to publish. */
    inSyncWithLive: boolean;
};

export function flowStatus(
    flow: AppFlowObjectKnown | undefined,
    liveFlow: AppFlowObjectKnown | undefined,
    previewFlowId: string | undefined,
): FlowStatus {
    return {
        published: liveFlow?.id === flow?.id,
        preview: !!previewFlowId && previewFlowId === flow?.id,
        inSyncWithLive:
            isEqual(nodeData(liveFlow?.nodes as AppNode[]), nodeData(flow?.nodes as AppNode[])) &&
            isEqual(edgeData(liveFlow?.edges as AppEdge[]), edgeData(flow?.edges as AppEdge[])),
    };
}

/** One guide plus where it stands — a row of the Guides table. */
export type GuideRow = { flow: AppFlowObjectKnown; status: FlowStatus };

/**
 * The health of a space's or equipment type's guide, at the altitude of a nav dot: is something
 * live, and if so is what's live current? A guide nobody published is `none` however many drafts
 * are sitting behind it — drafts aren't reaching anyone.
 */
export type GuideHealth = 'live' | 'pending' | 'none';

/**
 * Judged from the drafts and the live guide alone — no preview read, since a staged preview changes
 * a guide's BADGES but not whether what the app serves is current. That keeps a dot per row of a
 * long list to one document each.
 */
export function guideHealth(flows: AppFlowObjectKnown[], liveFlow: AppFlowObjectKnown | undefined): GuideHealth {
    if (!liveFlow) return 'none';
    const published = flows.find((flow) => flow.id === liveFlow.id);
    // Live with no draft behind it — someone deleted the draft. Still live, and still current, since
    // there is no newer copy to have drifted from it.
    if (!published) return 'live';
    return flowStatus(published, liveFlow, undefined).inSyncWithLive ? 'live' : 'pending';
}

/** Compare only what a guide MEANS — position on the canvas isn't a pending change. */
function nodeData(nodes?: AppNode[]) {
    return nodes?.map((n) => n.data);
}

function edgeData(edges?: AppEdge[]) {
    return edges?.map((e) => ({
        data: e.data,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
    }));
}
